import { BEM } from '@feedfm/feed-util';
import React, { useCallback, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { BreadcrumbItem, PageTitle } from '~/components/PageTitle/PageTitle';
import { title } from '~/util/site';
import { ContentSourceDetailBasics } from './_ContentSourceDetailBasics';
import { ContentSourceDetailRightsMappings } from '~/components/ContentSources/_ContentSourceDetail/_ContentSourceDetailRightsMappings';
import { ContentSourceDetailDefaultRights } from '~/components/ContentSources/_ContentSourceDetail/_ContentSourceDetailDefaultRights';
import { ContentSourceRightsMappingInput, UpdateContentSourceInput } from '~/graphqlTypes/globalTypes';
import { useMutation } from '@apollo/client';
import mutations from './ContentSourceDetailMutations.gql';
import { AudioFileAvailabilityFragment } from '~/graphqlTypes/AudioFileAvailabilityFragment';
import { deepEqualByValue } from '~/util/objects';
import { useAlert } from '~/hooks/useAlert';
import { getContentSourceUrl } from '~/components/ContentSourceLink/ContentSourceLink';
import { getGraphQLErrorMessages } from '~/util/errors';
import { ContentSourceRightsMappingFragment } from '~/graphqlTypes/ContentSourceRightsMappingFragment';
import { streamingRightFragmentToInput } from '~/transformers/streamingRightFragmentToInput';
import { ContentSourceDetailsFragment } from '~/graphqlTypes/ContentSourceDetailsFragment';

import './_ContentSourceDetail.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import {
  ContentSourceDetailUpdateMutation,
  ContentSourceDetailUpdateMutationVariables,
} from '~/graphqlTypes/ContentSourceDetailUpdateMutation';
import {
  ContentSourceDetailRecalculateSourcesMutation,
  ContentSourceDetailRecalculateSourcesMutationVariables,
} from '~/graphqlTypes/ContentSourceDetailRecalculateSourcesMutation';

// TODO: All streaming rights and all country codes should both get elevated to app-level context so
// we don't have to drill them down everywhere.
export interface ContentSourceDetailProps {
  source: ContentSourceDetailsFragment;
  className?: string;
}

const bem = new BEM( 'ops-catalog-sources-detail', [] );

const baseBreadcrumbs: BreadcrumbItem[] = [
  { href: '/dashboard', content: 'Dashboard' },
  { href: '/content/sources', content: 'Content Sources' },
];

interface SaveState {
  isSaving: boolean;
  saveSucceeded: boolean;
}

const rightsMappingToInput = ( {
  commercialModelType,
  streamingRights,
  useType,
}: ContentSourceRightsMappingFragment ): ContentSourceRightsMappingInput => ( {
  commercialModelType,
  streamingRightIds: streamingRights.map( ( { id } ) => id ),
  useType: useType,
} );

export const ContentSourceDetail: React.FC<ContentSourceDetailProps> = ( props ) => {
  const { className, source } = props;
  const { id, name } = source;

  const alertModel = useAlert();

  const [saveState, setSaveState] = useState<SaveState>( {
    isSaving: false,
    saveSucceeded: false,
  } );

  const alertModal = useAlert();

  const [updateContentSource] = useMutation<
  ContentSourceDetailUpdateMutation,
  ContentSourceDetailUpdateMutationVariables
  >( mutations.ContentSourceDetailUpdateMutation );

  const [recalculate, { loading: recalculating }] = useMutation<
  ContentSourceDetailRecalculateSourcesMutation,
  ContentSourceDetailRecalculateSourcesMutationVariables
  >( mutations.ContentSourceDetailRecalculateSourcesMutation );

  const handleRecalculateButton = useCallback( async () => {
    try {
      const { data } = await recalculate( { variables: { sourceIds: [source.id] } } );

      if ( !data ) {
        throw new Error( 'No data returned from server' );
      }

      const trackCount = data.calculateRightsForSources;
      alertModal( `Rights calculation queued for ${trackCount} tracks`, 'success' );
    } catch ( error: any ) {
      alertModal( error.message, 'danger' );
    }
  }, [recalculate, source, alertModal] );

  const save = useCallback(
    async ( input: UpdateContentSourceInput ) => {
      setSaveState( { isSaving: true, saveSucceeded: false } );

      try {
        const { errors } = await updateContentSource( {
          variables: {
            sourceId: source.id,
            input,
          },
        } );

        if ( errors && errors.length > 0 ) {
          throw new Error( getGraphQLErrorMessages( errors ).join( '\n' ) );
        }

        setSaveState( { isSaving: false, saveSucceeded: true } );
        alertModel( 'Content source saved', 'success' );
      } catch ( error: any ) {
        const errorMessages = getGraphQLErrorMessages( error );
        setSaveState( { isSaving: false, saveSucceeded: false } );
        errorMessages.forEach( ( errorMessage ) => alertModel( errorMessage, 'danger' ) );
      }
    },
    [updateContentSource, source, alertModel],
  );

  const handleUpdateDefaultRights = (
    newAvailability: AudioFileAvailabilityFragment,
    original?: AudioFileAvailabilityFragment,
  ) => {
    if ( original ) {
      const existingIndex = source.defaultRights.indexOf( original );

      if ( existingIndex === -1 ) {
        throw new Error( 'Could not find model to replace. This should not happen.' );
      }

      return save( {
        defaultRights: source.defaultRights
          .map( ( right, index ) => ( index !== existingIndex ? right : newAvailability ) )
          .map( streamingRightFragmentToInput ),
      } );
    } else {
      return save( {
        defaultRights: [...source.defaultRights, newAvailability].map( streamingRightFragmentToInput ),
      } );
    }
  };

  const handleDeleteDefaultRight = async ( right: AudioFileAvailabilityFragment ) => {
    await save( {
      defaultRights: source.defaultRights
        .filter( ( sourceRight ) => !deepEqualByValue( sourceRight, right ) )
        .map( streamingRightFragmentToInput ),
    } );
  };

  const handleDeleteRightsMapping = async ( mapping: ContentSourceRightsMappingFragment ) => {
    await save( {
      rightsMappings: source.rightsMappings
        .filter( ( { id: existingId } ) => existingId !== mapping.id )
        .map( rightsMappingToInput ),
    } );
  };

  const handleUpdateRightsMapping = async (
    newMapping: ContentSourceRightsMappingFragment,
    original?: ContentSourceRightsMappingFragment,
  ) => {
    if ( original ) {
      const existingIndex = source.rightsMappings.indexOf( original );

      if ( existingIndex === -1 ) {
        throw new Error( 'Could not find model to replace. This should not happen.' );
      }

      return save( {
        rightsMappings: source.rightsMappings
          .map( ( right, index ) => ( index !== existingIndex ? right : newMapping ) )
          .map( rightsMappingToInput ),
      } );
    } else {
      return save( {
        rightsMappings: [...source.rightsMappings, newMapping].map( rightsMappingToInput ),
      } );
    }
  };

  const breadcrumbs = [...baseBreadcrumbs, { href: getContentSourceUrl( id ), content: name }];

  return (
    <article data-testid="ops-catalog-sources-detail" className={ BEM.$( bem, className ) }>
      <Helmet>
        <title>{ title( `Catalog | Sources | ${name}` ) }</title>
      </Helmet>
      <PageTitle breadcrumbs={ breadcrumbs }>
        { name }
        <PageTitle.Id>{ id } </PageTitle.Id>
      </PageTitle>
      <Row>
        <Col>
          <ContentSourceDetailBasics
            isSaving={ saveState.isSaving }
            handleSave={ ( pricePerPlay ) => save( { pricePerPlay } ) }
            source={ source }
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <h3 className={ BEM.$( bem.el( 'section-heading' ) ) }>
            <FontAwesomeIcon icon={ faShieldHalved } /> Rights management
          </h3>
        </Col>
      </Row>
      <Row>
        <Col>
          <Button variant="primary" disabled={ recalculating } onClick={ handleRecalculateButton }>
            { ( recalculating && 'Queuing rights for calculation' ) || 'Queue rights for calculation' }
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <ContentSourceDetailDefaultRights
            source={ source }
            handleUpdateRights={ handleUpdateDefaultRights }
            handleDeleteRights={ handleDeleteDefaultRight }
          />
        </Col>
        <Col>
          <ContentSourceDetailRightsMappings
            source={ source }
            handleUpdateRightsMapping={ handleUpdateRightsMapping }
            handleDeleteRightsMapping={ handleDeleteRightsMapping }
          />
        </Col>
      </Row>
    </article>
  );
};
